/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import user from 'js/lib/user';

import { Dialog } from '@coursera/cds-core';
import { email, required, runValidations } from '@coursera/coursera-ui/lib/utils/validation';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import AddRecoveryEmailStep from 'bundles/enterprise-legacy-learner-home/components/modals/AddRecoveryEmailStep';
import ConfirmRecoveryEmailStep from 'bundles/enterprise-legacy-learner-home/components/modals/ConfirmRecoveryEmailStep';
import { STEPS } from 'bundles/enterprise-legacy-learner-home/constants/forcedSSORecoveryEmail';
import useAlternativeEmailModal from 'bundles/enterprise-legacy-learner-home/queryHooks/useAlternativeEmailModal';
import type { RecoveryEmail } from 'bundles/enterprise-legacy-learner-home/types/recoveryEmail';
import { insertRecoveryEmail as insertRecoveryEmailViaApi } from 'bundles/enterprise-legacy-learner-home/utils/RecoveryEmailAPIUtils';
import { useRecoveryEmail, useUserEmails } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

type WrapperProps = {
  userId: number;
  programId: string;
};

type Props = {
  userId: number;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  shouldDeferOnClose?: boolean;
  recoveryEmail: RecoveryEmail | undefined | null;
};

const buttonNames: Record<string, ButtonName> = {
  addRecoveryEmail: 'add_recovery_email_modal_button',
  closeRecoveryEmail: 'close_recovery_email_modal_button',
};
const inputName = 'recovery_email_modal_input';
export const ForcedSSORecoveryEmailModal = ({
  isOpen,
  userId,
  onUpdate,
  onClose,
  shouldDeferOnClose,
  recoveryEmail,
}: Props) => {
  const thirdPartyOrgContext = useThirdPartyOrganization();
  const defaultEmailField = recoveryEmail?.emailAddress;
  const [emailField, setEmailField] = useState<string>(defaultEmailField ?? '');
  const [emailFieldError, setEmailFieldError] = useState<string>('');
  const [isInsertingViaApi, setIsInsertingViaApi] = useState<boolean>(false);
  const primaryEmail = user.get().email_address;
  const track = useTracker();
  const [currentStep, setCurrentStep] = useState<keyof typeof STEPS>(
    defaultEmailField ? STEPS.CONFIRM_ADD : STEPS.ADD_EMAIL
  );
  const eventingEnterpriseProp = React.useMemo(
    () =>
      thirdPartyOrgContext?.slug && {
        enterprise: { organizationSlug: thirdPartyOrgContext.slug },
      },
    [thirdPartyOrgContext?.slug]
  );

  const addRecoveryEmailButton: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker(
    'view_button',
    {
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      button: { name: buttonNames.addRecoveryEmail },
      ...eventingEnterpriseProp,
    },
    [isOpen, currentStep]
  );

  const cancelButton: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker(
    'view_button',
    {
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      button: { name: buttonNames.closeRecoveryEmail },
      ...eventingEnterpriseProp,
    },
    [isOpen, currentStep]
  );

  React.useEffect(() => {
    if (isOpen && recoveryEmail?.emailAddress) {
      setCurrentStep(STEPS.CONFIRM_ADD);
      setEmailField(recoveryEmail.emailAddress);
    }
  }, [recoveryEmail?.emailAddress, isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      const isAddEmailStep = currentStep === STEPS.ADD_EMAIL;
      track('view_modal', {
        modal: { name: isAddEmailStep ? 'add_recovery_email_modal' : 'resend_recovery_email_modal' },
        ...eventingEnterpriseProp,
      });
    }
  }, [isOpen, currentStep, track, eventingEnterpriseProp]);

  const onEmailUpdate = () => {
    setEmailFieldError('');
    setCurrentStep(STEPS.CONFIRM_ADD);
    onUpdate();
  };

  const onCloseRecoveryEmail = () => {
    onClose();
    track('click_button', {
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      button: { name: buttonNames.closeRecoveryEmail },
      ...eventingEnterpriseProp,
    });
  };

  const onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEmailField(value);
    setEmailFieldError('');
  };

  const getValidationErrors = () => {
    if (emailField === primaryEmail) {
      return _t('You cannot use your primary email as a recovery email');
    }
    const errors = runValidations(emailField, [required, email]);
    return errors;
  };

  const onSubmitEmail = async () => {
    const errors = getValidationErrors();

    if (errors) {
      track('click_button', {
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        button: { name: buttonNames.addRecoveryEmail, clickStatus: 'invalid' },
        ...eventingEnterpriseProp,
      });
      track('validate_text_input', {
        textInput: { name: inputName },
        status: 'failure',
        errorCode: 'invalid_email',
        ...eventingEnterpriseProp,
      });
      setEmailFieldError(errors);
      return;
    }
    setEmailFieldError('');

    try {
      setIsInsertingViaApi(true);
      const res = await insertRecoveryEmailViaApi({ emailAddress: emailField, userId });
      if ('recoveryEmail' in res) {
        onEmailUpdate();
        track('click_button', {
          // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          button: { name: buttonNames.addRecoveryEmail, clickStatus: 'valid' },
          ...eventingEnterpriseProp,
        });
        track('validate_text_input', {
          textInput: { name: inputName },
          status: 'success',
          ...eventingEnterpriseProp,
        });
        return;
      }
      // Fallback to error state if response is unexpected
      setEmailFieldError(_t('There was an error while adding the email, please try again'));
      track('click_button', {
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        button: { name: buttonNames.addRecoveryEmail, clickStatus: 'invalid' },
        ...eventingEnterpriseProp,
      });
      track('validate_text_input', {
        textInput: { name: inputName },
        status: 'failure',
        errorCode: 'network_error',
        ...eventingEnterpriseProp,
      });
    } catch {
      setEmailFieldError(_t('There was an error while adding the email, please try again'));
      track('click_button', {
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        button: { name: buttonNames.addRecoveryEmail, clickStatus: 'invalid' },
        ...eventingEnterpriseProp,
      });
      track('validate_text_input', {
        textInput: { name: inputName },
        status: 'failure',
        errorCode: 'network_error',
        ...eventingEnterpriseProp,
      });
    } finally {
      setIsInsertingViaApi(false);
    }
  };

  const getModalContent = () => {
    if (currentStep === STEPS.ADD_EMAIL) {
      return (
        <AddRecoveryEmailStep
          emailField={emailField}
          onChangeEmail={onChangeEmail}
          getValidationErrors={getValidationErrors}
          onSubmitEmail={onSubmitEmail}
          emailFieldError={emailFieldError}
          loading={isInsertingViaApi}
          onClose={onCloseRecoveryEmail}
          addRecoveryEmailButtonRef={addRecoveryEmailButton}
          cancelButtonRef={cancelButton}
          shouldDeferOnClose={shouldDeferOnClose}
        />
      );
    }

    return (
      <ConfirmRecoveryEmailStep
        userId={userId}
        emailField={emailField}
        onUpdateEmailClick={() => setCurrentStep(STEPS.ADD_EMAIL)}
        onClose={onCloseRecoveryEmail}
      />
    );
  };

  return (
    <Dialog aria-describedby="description" aria-labelledby="title" open={isOpen} variant="standard" onClose={onClose}>
      {getModalContent()}
    </Dialog>
  );
};

export const ForcedSSORecoveryEmailModalWrapper = ({ userId, programId }: WrapperProps) => {
  const { userEmails = [], refetchUserEmails } = useUserEmails();
  const { recoveryEmail } = useRecoveryEmail();
  const { isOpen, onClose, shouldDeferOnClose } = useAlternativeEmailModal(
    userId,
    userEmails,
    programId,
    recoveryEmail
  );

  const onUpdate = () => {
    refetchUserEmails?.();
  };

  return (
    <ForcedSSORecoveryEmailModal
      userId={userId}
      isOpen={isOpen}
      onClose={onClose}
      onUpdate={onUpdate}
      shouldDeferOnClose={shouldDeferOnClose}
      recoveryEmail={recoveryEmail}
    />
  );
};

export default ForcedSSORecoveryEmailModalWrapper;
